import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useMessages } from "../contexts/MessageContext";
import { calculateGestationalAge } from "../utils/dateUtils";
import { DASHBOARD_CARDS, loadDashboardSettings } from "../utils/dashboardSettings";
import config from '../config';
import { appointmentApi } from "../utils/appointmentApi";

const DashboardCard = ({ title, children }) => (
  <div className="bg-white rounded-2xl shadow p-4">
    <h2 className="text-xl font-semibold mb-2">{title}</h2>
    {children}
  </div>
);

// Card components
const UpcomingAppointments = () => {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAppointments = async () => {
      if (!user?.sub) return;
      
      try {
        setLoading(true);
        setError(null);
        const data = await appointmentApi.getMidwifeAppointments(user.sub);
        
        // Sort by date and take only upcoming appointments (limit to 5)
        const now = new Date();
        const upcomingAppointments = data
          .filter(apt => {
            const aptDate = new Date(apt.startTime);
            return aptDate > now;
          })
          .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
          .slice(0, 5);
        
        setAppointments(upcomingAppointments);
      } catch (err) {
        console.error('Failed to fetch appointments:', err);
        setError('Failed to load appointments');
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, [user?.sub]);

  const formatAppointmentDate = (dateTime) => {
    const date = new Date(dateTime);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Check if the appointment is today or tomorrow
    if (date.toDateString() === today.toDateString()) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    }

    // For other dates
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <DashboardCard title={DASHBOARD_CARDS.UPCOMING_APPOINTMENTS.title}>
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500"></div>
        </div>
      ) : error ? (
        <p className="text-sm text-red-500 py-2">{error}</p>
      ) : appointments.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500">No upcoming appointments</p>
          <button
            onClick={() => navigate('/midwife/appointments')}
            className="mt-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
          >
            Schedule an appointment
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {appointments.map((apt) => (
            <div 
              key={apt.id}
              className="flex flex-col hover:bg-gray-50 p-2 rounded cursor-pointer transition-colors"
              onClick={() => navigate('/midwife/appointments')}
            >
              <div className="flex justify-between items-center">
                <span className="font-medium">{apt.clientName || 'Unknown Client'}</span>
                <span className="text-emerald-600 text-sm font-medium">
                  {formatAppointmentDate(apt.startTime)}
                </span>
              </div>
              {apt.type && (
                <span className="text-sm text-gray-500">{apt.type}</span>
              )}
            </div>
          ))}
          <button
            onClick={() => navigate('/midwife/appointments')}
            className="w-full mt-3 text-sm text-emerald-600 hover:text-emerald-700 font-medium text-center pt-2 border-t"
          >
            View all appointments →
          </button>
        </div>
      )}
    </DashboardCard>
  );
};

const QuickAccess = ({ onNavigate }) => (
  <DashboardCard title={DASHBOARD_CARDS.QUICK_ACCESS.title}>
    {[
      { label: "Start New Chart", action: () => {} },
      { label: "Add Client", action: () => onNavigate("/midwife/clients/new") },
      { label: "Send Message", action: () => {} },
      { label: "View Labs", action: () => {} }
    ].map(btn => (
      <button
        key={btn.label}
        onClick={btn.action}
        className="w-full border border-emerald-700 text-emerald-700
                  hover:bg-emerald-50 active:bg-emerald-100
                  font-semibold py-2 px-4 rounded mb-2 transition-colors"
      >
        {btn.label}
      </button>
    ))}
  </DashboardCard>
);

const Clients36Plus = ({ clients, loading, onClientClick }) => (
  <DashboardCard title={DASHBOARD_CARDS.CLIENTS_36_PLUS.title}>
    {loading ? (
      <p className="text-sm text-gray-500">Loading clients…</p>
    ) : clients.length === 0 ? (
      <p className="text-sm text-gray-500">No clients at 36+ weeks.</p>
    ) : (
      <ul className="space-y-1">
        {clients.map(({ id, fullName, gestationalAge }) => {
          const { weeks, days } = gestationalAge;
          const is40 = weeks >= 40;
          return (
            <li
              key={id}
              onClick={() => onClientClick(id)}
              className={`cursor-pointer hover:text-emerald-700 ${
                is40 ? "font-bold text-red-600" : ""
              }`}
            >
              {fullName} – {weeks}w{days}d
            </li>
          );
        })}
      </ul>
    )}
  </DashboardCard>
);

const TasksReminders = () => (
  <DashboardCard title={DASHBOARD_CARDS.TASKS_REMINDERS.title}>
    <ul className="list-disc list-inside space-y-1">
      <li>Follow up with Emily Johnson</li>
      <li>Review lab results for Sarah</li>
      <li>Complete charting for Jane</li>
    </ul>
  </DashboardCard>
);

const RecentActivity = () => (
  <DashboardCard title={DASHBOARD_CARDS.RECENT_ACTIVITY.title}>
    <ul className="space-y-1 text-sm">
      <li>Chart completed for Jane Doe</li>
      <li>New lab result for Sarah Smith</li>
      <li>Client Emily Johnson added</li>
    </ul>
  </DashboardCard>
);

const Messages = () => {
  const { messages, isConnected } = useMessages();
  const navigate = useNavigate();
  
  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return 'just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays === 1) return 'yesterday';
    return date.toLocaleDateString();
  };

  const truncateMessage = (content, maxLength = 30) => {
    if (!content) return '';
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  // Get the 3 most recent messages
  const recentMessages = messages
    .filter(msg => msg && msg.timestamp) // Ensure valid messages only
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .slice(0, 3);

  return (
    <DashboardCard title={DASHBOARD_CARDS.MESSAGES.title}>
      {!isConnected ? (
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500"></div>
        </div>
      ) : recentMessages.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500">No recent messages</p>
          <button
            onClick={() => navigate('/midwife/messages')}
            className="mt-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
          >
            Start a conversation
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {recentMessages.map((message) => (
            <div 
              key={message.id || message.timestamp}
              className="flex justify-between items-start hover:bg-gray-50 p-2 rounded cursor-pointer transition-colors"
              onClick={() => navigate('/midwife/messages')}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {message.isOutgoing ? message.recipientName : message.senderName || 'Unknown'}
                </p>
                <p className="text-sm text-gray-600 truncate">
                  {truncateMessage(message.content)}
                </p>
              </div>
              <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                {formatMessageTime(message.timestamp)}
              </span>
            </div>
          ))}
          <button
            onClick={() => navigate('/midwife/messages')}
            className="w-full mt-3 text-sm text-emerald-600 hover:text-emerald-700 font-medium text-center pt-2 border-t"
          >
            View all messages →
          </button>
        </div>
      )}
    </DashboardCard>
  );
};

const CalendarOverview = () => (
  <DashboardCard title={DASHBOARD_CARDS.CALENDAR_OVERVIEW.title}>
    <p className="text-gray-600">Calendar integration coming soon...</p>
  </DashboardCard>
);

const StatsOverview = () => (
  <DashboardCard title={DASHBOARD_CARDS.STATS_OVERVIEW.title}>
    <div className="grid grid-cols-2 gap-4 text-center">
      <div>
        <p className="text-2xl font-bold text-emerald-700">24</p>
        <p className="text-sm text-gray-600">Active Clients</p>
      </div>
      <div>
        <p className="text-2xl font-bold text-emerald-700">8</p>
        <p className="text-sm text-gray-600">Due This Month</p>
      </div>
    </div>
  </DashboardCard>
);

export default function MidwifeDashboard() {
  const { user, logout } = useAuth();
  const midwifeId = user?.sub;
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [dashboardSettings, setDashboardSettings] = useState({});

  const handleLogout = () => {
    localStorage.removeItem('velaToken');
    logout();
    navigate('/login');
  };

  // Load dashboard settings
  useEffect(() => {
    if (midwifeId) {
      const settings = loadDashboardSettings(midwifeId);
      setDashboardSettings(settings);
    }
  }, [midwifeId]);

  // Fetch clients
  useEffect(() => {
    if (!midwifeId) return;

    (async () => {
      try {
        const res = await fetch(`${config.api.url}/midwives/${midwifeId}/clients`);
        if (!res.ok) throw new Error("Failed to fetch clients.");
        const data = await res.json();

        const soonest = data
          .filter(c => c.dueDate)
          .map(c => ({ ...c, gestationalAge: calculateGestationalAge(c.dueDate) }))
          .filter(c => c.gestationalAge.is36Plus)
          .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

        setClients(soonest);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [midwifeId]);

  // Get enabled cards based on settings
  const getEnabledCards = () => {
    const cards = [];
    
    if (dashboardSettings[DASHBOARD_CARDS.UPCOMING_APPOINTMENTS.id] ?? DASHBOARD_CARDS.UPCOMING_APPOINTMENTS.defaultEnabled) {
      cards.push(<UpcomingAppointments key="appointments" />);
    }
    
    if (dashboardSettings[DASHBOARD_CARDS.QUICK_ACCESS.id] ?? DASHBOARD_CARDS.QUICK_ACCESS.defaultEnabled) {
      cards.push(<QuickAccess key="quick-access" onNavigate={navigate} />);
    }
    
    if (dashboardSettings[DASHBOARD_CARDS.CLIENTS_36_PLUS.id] ?? DASHBOARD_CARDS.CLIENTS_36_PLUS.defaultEnabled) {
      cards.push(
        <Clients36Plus
          key="clients-36"
          clients={clients}
          loading={loading}
          onClientClick={(id) => navigate(`/charting/${id}`)}
        />
      );
    }
    
    if (dashboardSettings[DASHBOARD_CARDS.TASKS_REMINDERS.id] ?? DASHBOARD_CARDS.TASKS_REMINDERS.defaultEnabled) {
      cards.push(<TasksReminders key="tasks" />);
    }
    
    if (dashboardSettings[DASHBOARD_CARDS.RECENT_ACTIVITY.id] ?? DASHBOARD_CARDS.RECENT_ACTIVITY.defaultEnabled) {
      cards.push(<RecentActivity key="activity" />);
    }
    
    if (dashboardSettings[DASHBOARD_CARDS.MESSAGES.id] ?? DASHBOARD_CARDS.MESSAGES.defaultEnabled) {
      cards.push(<Messages key="messages" />);
    }
    
    if (dashboardSettings[DASHBOARD_CARDS.CALENDAR_OVERVIEW.id] ?? DASHBOARD_CARDS.CALENDAR_OVERVIEW.defaultEnabled) {
      cards.push(<CalendarOverview key="calendar" />);
    }
    
    if (dashboardSettings[DASHBOARD_CARDS.STATS_OVERVIEW.id] ?? DASHBOARD_CARDS.STATS_OVERVIEW.defaultEnabled) {
      cards.push(<StatsOverview key="stats" />);
    }
    
    return cards;
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100 text-gray-900">
      {/* Mobile App Bar */}
      <div className="md:hidden flex justify-between items-center bg-emerald-700 text-white px-4 py-3">
        <h1 className="text-2xl font-bold">Vela</h1>
        <button onClick={() => setSidebarOpen(!isSidebarOpen)}>☰</button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-gray-200 text-emerald-700
          p-6 transform transition-transform duration-300 ease-in-out
          md:relative md:translate-x-0 ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} md:block`}
      >
        <div className="flex flex-col h-full">
          <h1 className="text-3xl font-extrabold tracking-tight mb-6">Vela</h1>
          <nav className="flex flex-col gap-1 text-sm font-medium flex-grow">
            {[
              { label: "Dashboard", to: "/midwife" },
              { label: "Clients", to: "/midwife/clients" },
              { label: "Appointments", to: "/midwife/appointments" },
              { label: "Messages", to: "/midwife/messages" },
              { label: "Charts", to: "/midwife/charts" },
              { label: "Labs", to: "/midwife/labs" },
              { label: "Documents", to: "/midwife/documents" },
              { label: "Settings", to: "/midwife/settings" }
            ].map(link => (
              <Link
                key={link.label}
                to={link.to}
                className="px-3 py-2 rounded
                           hover:bg-emerald-50 hover:text-emerald-800
                           transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </nav>
          
          <button
            onClick={handleLogout}
            className="mt-8 py-2 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded transition-colors duration-200 flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h5v-2H4V5h4V3H3zm12.293 2.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              <path fillRule="evenodd" d="M9 3a1 1 0 000 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L13 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {getEnabledCards()}
      </main>
    </div>
  );
}
